import { OTP_DELIVERY_CHANNELS, OTP_VERIFICATION_FAILURE_REASON } from '../services/exports/Constants';

// Used when the store carries no provider channels — e.g. an older backend response.
export const FALLBACK_OTP_CHANNELS = [OTP_DELIVERY_CHANNELS.WHATSAPP, OTP_DELIVERY_CHANNELS.SMS];

export interface OtpFailureDetails {
  // Bucketed reason — safe to switch on and to use as a translation key.
  failure_reason: OTP_VERIFICATION_FAILURE_REASON;
  // Exactly what the backend put in `error_code` (null when omitted). Logged to Mixpanel so a
  // new/unmapped backend code stays visible as its real value instead of hiding behind `unknown`.
  raw_error_code: string | null;
  // HTTP status of the failed /login response (null when the request never reached the server).
  status: number | null;
}

const KNOWN_REASONS: string[] = Object.values(OTP_VERIFICATION_FAILURE_REASON);

/**
 * Resolves a failed /login response into a structured OTP failure for analytics + UI.
 *
 * Unlike the previous behaviour, an omitted or unrecognised `error_code` is no longer collapsed
 * into `incorrect_code`: an unmapped server code becomes `unknown` and a request that never
 * reached the server becomes `network_error`. The raw code and HTTP status are always preserved
 * so Mixpanel can distinguish "omitted" (`raw_error_code` null) from "unrecognised value".
 */
export function resolveOtpFailure(result: any): OtpFailureDetails {
  const rawCode = result?.response?.response?.data?.error_code ?? null;
  const status = result?.status ?? result?.response?.response?.status ?? null;

  // The request reached the server if it produced an HTTP response (or at least a status code);
  // otherwise it failed in transit (offline, DNS, timeout, CORS, ...).
  const reachedServer = Boolean(result?.response?.response) || status != null;

  let failure_reason: OTP_VERIFICATION_FAILURE_REASON;

  if (KNOWN_REASONS.includes(rawCode)) {
    failure_reason = rawCode;
  } else if (!reachedServer) {
    failure_reason = OTP_VERIFICATION_FAILURE_REASON.NETWORK_ERROR;
  } else {
    failure_reason = OTP_VERIFICATION_FAILURE_REASON.UNKNOWN;
  }

  return { failure_reason, raw_error_code: rawCode, status };
}

/**
 * Where the code was actually sent, for the "we sent it to ..." line.
 *
 * The phone number is the identity key on every channel, but on email it is not the
 * destination — showing it there would point the customer at a phone that never received
 * anything. The API masks the address (`a•••@g•••.com`) before it reaches us, so it is safe
 * to render. Falls back to the phone number when the response carried no address.
 */
export function resolveOtpDestination(
  channel: OTP_DELIVERY_CHANNELS,
  phoneNumber: string,
  deliveryAddress?: string,
): string {
  if (channel !== OTP_DELIVERY_CHANNELS.EMAIL) {
    return phoneNumber;
  }

  return deliveryAddress || phoneNumber;
}

/**
 * Channels the company's provider offers, in the order they should be presented.
 *
 * A provider switched on for email OTP offers email and nothing else — the channel
 * exists to remove the Twilio bill for white-label partners, which keeping the phone
 * channels alongside it would not do. The fallback covers a response from a backend
 * that predates the field.
 */
export function resolveOtpChannels(channels?: OTP_DELIVERY_CHANNELS[]): OTP_DELIVERY_CHANNELS[] {
  return channels?.length ? channels : FALLBACK_OTP_CHANNELS;
}

/**
 * Channel to pre-select. WhatsApp stays the preferred default wherever it is offered;
 * a provider that does not offer it (an email-only partner) gets its first channel, so
 * the initial request is never one the backend would reject with a 422.
 */
export function resolveDefaultOtpChannel(channels: OTP_DELIVERY_CHANNELS[]): OTP_DELIVERY_CHANNELS {
  return channels.includes(OTP_DELIVERY_CHANNELS.WHATSAPP) ? OTP_DELIVERY_CHANNELS.WHATSAPP : channels[0];
}
